import { supabase } from '../lib/supabase';

/**
 * Order Tracking Service
 * Handles real-time order tracking, shipping integration, and delivery confirmations
 */

export const orderTrackingService = {
  /**
   * Get all shipments (Admin only)
   */
  async getAllShipments() {
    try {
      const { data, error } = await supabase
        ?.from('order_shipments')
        ?.select(`
          *,
          shipping_carriers (*),
          tracking_events (
            *
          ),
          order:orders (
            *,
            user:user_profiles (
              full_name,
              email,
              avatar_url
            )
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(shipment => ({
        id: shipment?.id,
        orderId: shipment?.order_id,
        trackingNumber: shipment?.tracking_number,
        status: shipment?.status,
        carrier: shipment?.shipping_carriers ? {
          id: shipment?.shipping_carriers?.id,
          name: shipment?.shipping_carriers?.name,
          code: shipment?.shipping_carriers?.code,
          trackingUrlTemplate: shipment?.shipping_carriers?.tracking_url_template,
          logoUrl: shipment?.shipping_carriers?.logo_url
        } : null,
        order: shipment?.order ? {
          id: shipment?.order?.id,
          userId: shipment?.order?.user_id,
          totalAmount: parseFloat(shipment?.order?.total_amount || 0),
          status: shipment?.order?.status,
          shippingAddress: shipment?.order?.shipping_address,
          user: shipment?.order?.user ? {
            fullName: shipment?.order?.user?.full_name,
            email: shipment?.order?.user?.email,
            avatarUrl: shipment?.order?.user?.avatar_url
          } : null
        } : null,
        shippedAt: shipment?.shipped_at,
        estimatedDeliveryAt: shipment?.estimated_delivery_at,
        actualDeliveryAt: shipment?.actual_delivery_at,
        shippingLabelUrl: shipment?.shipping_label_url,
        weightKg: parseFloat(shipment?.weight_kg || 0),
        dimensionsCm: shipment?.dimensions_cm,
        shippingCost: parseFloat(shipment?.shipping_cost || 0),
        notes: shipment?.notes,
        trackingEvents: shipment?.tracking_events?.map(event => ({
          id: event?.id,
          eventType: event?.event_type,
          eventDescription: event?.event_description,
          location: event?.location,
          latitude: event?.latitude ? parseFloat(event?.latitude) : null,
          longitude: event?.longitude ? parseFloat(event?.longitude) : null,
          eventTimestamp: event?.event_timestamp,
          carrierEventCode: event?.carrier_event_code,
          metadata: event?.metadata
        })) || [],
        createdAt: shipment?.created_at,
        updatedAt: shipment?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching all shipments:', error);
      throw error;
    }
  },

  /**
   * Get user's shipments
   */
  async getUserShipments() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('order_shipments')?.select(`*,shipping_carriers (*),tracking_events (*),order:orders!inner (id,user_id,total_amount,status,shipping_address)`)?.eq('order.user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(shipment => ({
        id: shipment?.id,
        orderId: shipment?.order_id,
        trackingNumber: shipment?.tracking_number,
        status: shipment?.status,
        carrier: shipment?.shipping_carriers ? {
          id: shipment?.shipping_carriers?.id,
          name: shipment?.shipping_carriers?.name,
          code: shipment?.shipping_carriers?.code,
          trackingUrlTemplate: shipment?.shipping_carriers?.tracking_url_template,
          logoUrl: shipment?.shipping_carriers?.logo_url
        } : null,
        order: shipment?.order ? {
          id: shipment?.order?.id,
          userId: shipment?.order?.user_id,
          totalAmount: parseFloat(shipment?.order?.total_amount || 0),
          status: shipment?.order?.status,
          shippingAddress: shipment?.order?.shipping_address
        } : null,
        shippedAt: shipment?.shipped_at,
        estimatedDeliveryAt: shipment?.estimated_delivery_at,
        actualDeliveryAt: shipment?.actual_delivery_at,
        shippingLabelUrl: shipment?.shipping_label_url,
        weightKg: parseFloat(shipment?.weight_kg || 0),
        dimensionsCm: shipment?.dimensions_cm,
        shippingCost: parseFloat(shipment?.shipping_cost || 0),
        notes: shipment?.notes,
        trackingEvents: shipment?.tracking_events?.map(event => ({
          id: event?.id,
          eventType: event?.event_type,
          eventDescription: event?.event_description,
          location: event?.location,
          latitude: event?.latitude ? parseFloat(event?.latitude) : null,
          longitude: event?.longitude ? parseFloat(event?.longitude) : null,
          eventTimestamp: event?.event_timestamp,
          carrierEventCode: event?.carrier_event_code,
          metadata: event?.metadata
        })) || [],
        createdAt: shipment?.created_at,
        updatedAt: shipment?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user shipments:', error);
      throw error;
    }
  },

  /**
   * Get all shipments for an order
   */
  async getOrderShipments(orderId) {
    try {
      const { data, error } = await supabase
        ?.from('order_shipments')?.select(`*,shipping_carriers (*),tracking_events (*)`)?.eq('order_id', orderId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(shipment => ({
        id: shipment?.id,
        orderId: shipment?.order_id,
        trackingNumber: shipment?.tracking_number,
        status: shipment?.status,
        carrier: shipment?.shipping_carriers ? {
          id: shipment?.shipping_carriers?.id,
          name: shipment?.shipping_carriers?.name,
          code: shipment?.shipping_carriers?.code,
          trackingUrlTemplate: shipment?.shipping_carriers?.tracking_url_template,
          logoUrl: shipment?.shipping_carriers?.logo_url
        } : null,
        shippedAt: shipment?.shipped_at,
        estimatedDeliveryAt: shipment?.estimated_delivery_at,
        actualDeliveryAt: shipment?.actual_delivery_at,
        shippingLabelUrl: shipment?.shipping_label_url,
        weightKg: parseFloat(shipment?.weight_kg || 0),
        dimensionsCm: shipment?.dimensions_cm,
        shippingCost: parseFloat(shipment?.shipping_cost || 0),
        notes: shipment?.notes,
        trackingEvents: shipment?.tracking_events?.map(event => ({
          id: event?.id,
          eventType: event?.event_type,
          eventDescription: event?.event_description,
          location: event?.location,
          latitude: event?.latitude ? parseFloat(event?.latitude) : null,
          longitude: event?.longitude ? parseFloat(event?.longitude) : null,
          eventTimestamp: event?.event_timestamp,
          carrierEventCode: event?.carrier_event_code,
          metadata: event?.metadata
        })) || [],
        createdAt: shipment?.created_at,
        updatedAt: shipment?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching order shipments:', error);
      throw error;
    }
  },

  /**
   * Get shipment by tracking number
   */
  async getShipmentByTrackingNumber(trackingNumber) {
    try {
      const { data, error } = await supabase
        ?.from('order_shipments')?.select(`*,shipping_carriers (*),tracking_events (*),orders (id,total_amount,status,shipping_address)`)?.eq('tracking_number', trackingNumber)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        orderId: data?.order_id,
        trackingNumber: data?.tracking_number,
        status: data?.status,
        carrier: data?.shipping_carriers ? {
          id: data?.shipping_carriers?.id,
          name: data?.shipping_carriers?.name,
          code: data?.shipping_carriers?.code,
          trackingUrlTemplate: data?.shipping_carriers?.tracking_url_template,
          logoUrl: data?.shipping_carriers?.logo_url
        } : null,
        order: data?.orders ? {
          id: data?.orders?.id,
          totalAmount: parseFloat(data?.orders?.total_amount),
          status: data?.orders?.status,
          shippingAddress: data?.orders?.shipping_address
        } : null,
        shippedAt: data?.shipped_at,
        estimatedDeliveryAt: data?.estimated_delivery_at,
        actualDeliveryAt: data?.actual_delivery_at,
        trackingEvents: data?.tracking_events?.map(event => ({
          id: event?.id,
          eventType: event?.event_type,
          eventDescription: event?.event_description,
          location: event?.location,
          eventTimestamp: event?.event_timestamp
        })) || []
      };
    } catch (error) {
      console.error('Error fetching shipment by tracking number:', error);
      throw error;
    }
  },

  /**
   * Get user's order tracking summary
   */
  async getUserOrderTracking() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.rpc('get_user_order_tracking', { p_user_id: user?.id });

      if (error) throw error;

      return data?.map(item => ({
        orderId: item?.order_id,
        orderTotal: parseFloat(item?.order_total),
        orderStatus: item?.order_status,
        shipmentId: item?.shipment_id,
        trackingNumber: item?.tracking_number,
        carrierName: item?.carrier_name,
        shipmentStatus: item?.shipment_status,
        estimatedDelivery: item?.estimated_delivery,
        latestEvent: item?.latest_event,
        latestLocation: item?.latest_location,
        latestTimestamp: item?.latest_timestamp
      })) || [];
    } catch (error) {
      console.error('Error fetching user order tracking:', error);
      throw error;
    }
  },

  /**
   * Get delivery confirmation for shipment
   */
  async getDeliveryConfirmation(shipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('delivery_confirmations')
        ?.select('*')
        ?.eq('shipment_id', shipmentId)
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') return null; // No confirmation yet
        throw error;
      }

      return {
        id: data?.id,
        shipmentId: data?.shipment_id,
        recipientName: data?.recipient_name,
        recipientSignatureUrl: data?.recipient_signature_url,
        deliveryPhotoUrls: data?.delivery_photo_urls || [],
        deliveryNotes: data?.delivery_notes,
        gpsLatitude: data?.gps_latitude ? parseFloat(data?.gps_latitude) : null,
        gpsLongitude: data?.gps_longitude ? parseFloat(data?.gps_longitude) : null,
        deliveryTimestamp: data?.delivery_timestamp,
        customerRating: data?.customer_rating,
        customerFeedback: data?.customer_feedback,
        status: data?.status,
        disputeReason: data?.dispute_reason,
        resolvedAt: data?.resolved_at
      };
    } catch (error) {
      console.error('Error fetching delivery confirmation:', error);
      throw error;
    }
  },

  /**
   * Submit customer rating and feedback for delivery
   */
  async submitDeliveryFeedback(shipmentId, rating, feedback) {
    try {
      const { data, error } = await supabase
        ?.from('delivery_confirmations')
        ?.update({
          customer_rating: rating,
          customer_feedback: feedback,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('shipment_id', shipmentId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        data: {
          id: data?.id,
          customerRating: data?.customer_rating,
          customerFeedback: data?.customer_feedback
        }
      };
    } catch (error) {
      console.error('Error submitting delivery feedback:', error);
      throw error;
    }
  },

  /**
   * Get order status history
   */
  async getOrderStatusHistory(orderId) {
    try {
      const { data, error } = await supabase
        ?.from('order_status_history')
        ?.select(`
          *,
          user_profiles (
            full_name,
            email
          )
        `)
        ?.eq('order_id', orderId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(history => ({
        id: history?.id,
        orderId: history?.order_id,
        oldStatus: history?.old_status,
        newStatus: history?.new_status,
        changedBy: history?.user_profiles ? {
          fullName: history?.user_profiles?.full_name,
          email: history?.user_profiles?.email
        } : null,
        changeReason: history?.change_reason,
        metadata: history?.metadata,
        createdAt: history?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching order status history:', error);
      throw error;
    }
  },

  /**
   * Get all shipping carriers
   */
  async getShippingCarriers() {
    try {
      const { data, error } = await supabase
        ?.from('shipping_carriers')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('name');

      if (error) throw error;

      return data?.map(carrier => ({
        id: carrier?.id,
        name: carrier?.name,
        code: carrier?.code,
        trackingUrlTemplate: carrier?.tracking_url_template,
        apiEndpoint: carrier?.api_endpoint,
        isActive: carrier?.is_active,
        logoUrl: carrier?.logo_url,
        estimatedDeliveryDays: carrier?.estimated_delivery_days
      })) || [];
    } catch (error) {
      console.error('Error fetching shipping carriers:', error);
      throw error;
    }
  },

  /**
   * Get user's delivery preferences
   */
  async getDeliveryPreferences() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('delivery_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') return null; // No preferences yet
        throw error;
      }

      return {
        id: data?.id,
        userId: data?.user_id,
        preferredDeliveryTime: data?.preferred_delivery_time,
        leaveAtDoor: data?.leave_at_door,
        signatureRequired: data?.signature_required,
        specialInstructions: data?.special_instructions,
        notificationEmail: data?.notification_email,
        notificationSms: data?.notification_sms,
        notificationPush: data?.notification_push
      };
    } catch (error) {
      console.error('Error fetching delivery preferences:', error);
      throw error;
    }
  },

  /**
   * Update delivery preferences
   */
  async updateDeliveryPreferences(preferences) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('delivery_preferences')
        ?.upsert({
          user_id: user?.id,
          preferred_delivery_time: preferences?.preferredDeliveryTime,
          leave_at_door: preferences?.leaveAtDoor,
          signature_required: preferences?.signatureRequired,
          special_instructions: preferences?.specialInstructions,
          notification_email: preferences?.notificationEmail,
          notification_sms: preferences?.notificationSms,
          notification_push: preferences?.notificationPush,
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        data: {
          id: data?.id,
          preferredDeliveryTime: data?.preferred_delivery_time,
          leaveAtDoor: data?.leave_at_door
        }
      };
    } catch (error) {
      console.error('Error updating delivery preferences:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time tracking updates for a shipment
   */
  subscribeToShipmentUpdates(shipmentId, callback) {
    const channel = supabase
      ?.channel(`shipment:${shipmentId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tracking_events',
          filter: `shipment_id=eq.${shipmentId}`
        },
        (payload) => {
          callback({
            type: 'tracking_event',
            data: {
              id: payload?.new?.id,
              eventType: payload?.new?.event_type,
              eventDescription: payload?.new?.event_description,
              location: payload?.new?.location,
              eventTimestamp: payload?.new?.event_timestamp
            }
          });
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'order_shipments',
          filter: `id=eq.${shipmentId}`
        },
        (payload) => {
          callback({
            type: 'shipment_update',
            data: {
              id: payload?.new?.id,
              status: payload?.new?.status,
              estimatedDeliveryAt: payload?.new?.estimated_delivery_at,
              actualDeliveryAt: payload?.new?.actual_delivery_at
            }
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Generate tracking URL for carrier
   */
  getTrackingUrl(carrier, trackingNumber) {
    if (!carrier?.trackingUrlTemplate || !trackingNumber) return null;
    return carrier?.trackingUrlTemplate?.replace('{tracking_number}', trackingNumber);
  }
};

export default orderTrackingService;